import { AxiosError } from 'axios'
import { DEFAULT_ALLOCATION, ROOM_GUESTS_SEPARATOR } from '../constants'
import { CreateSearchParams, RoomAllocation } from '../types'
import {
	DateObject,
	GeoAreaFilter,
	HotelSearchResponseStatusEnum,
	PriceRangeFilter,
	StringListFilter,
} from '../types/skyscanner-wrapper-api'

export const paramStringToEnumArray = <E = StringListFilter>(
	paramString?: string,
	enumList: { id: number; value: any }[] = []
): E | undefined => {
	if (!paramString) return
	const values = paramString
		.split('|')
		.map((param) => enumList.find((i) => i.id === parseInt(param))?.value)
		.filter((v) => v !== undefined)
	return values.length ? ({ values } as E) : undefined
}

export const mnMxToPriceRangeFilter = (
	nights: number,
	mn?: number,
	mx?: number
): PriceRangeFilter | undefined => {
	if (mn === undefined && mx === undefined) return
	const priceRangeFilter: PriceRangeFilter = {}
	if (mn !== undefined) priceRangeFilter.minValue = mn * nights
	if (mx !== undefined) priceRangeFilter.maxValue = mx * nights
	return priceRangeFilter
}

export const polygonToGeoAreaFilter = (
	polygon?: string
): GeoAreaFilter | undefined => {
	if (!polygon) return
	const pParts = polygon.split(':')
	if (pParts.length < 2) return
	const coords = pParts[1]
		.split(';')
		.map((sCoord) => {
			const values = sCoord.split(',')
			if (values.length < 2) return
			return values.map(parseFloat)
		})
		.filter((c) => !!c)
	if (coords.length < 4) return
	const filter = {
		// Opposite to documentation to work: south-east first, north-west second
		coordinates: [
			{
				latitude: coords[2][0],
				longitude: coords[2][1],
			},
			{
				latitude: coords[0][0],
				longitude: coords[0][1],
			},
		],
	} as GeoAreaFilter
	// Avoiding 500 on skyscanner
	if (filter.coordinates[0].latitude === filter.coordinates[1].latitude)
		filter.coordinates[0].latitude -= 0.001
	if (filter.coordinates[0].longitude === filter.coordinates[1].longitude)
		filter.coordinates[0].longitude += 0.001
	return filter
}

export const getUserRating = (rating?: string): string | null => {
	if (!rating || rating[0] === '0' || rating === 'undefined') {
		return null
	}
	return rating
}

export const dateStringToObj = (date: string): DateObject => {
	const dateParts = date.split('-')
	// TODO: This is fallback for invalid date string. Check if fallback to new Date() is better.
	dateParts.length = 3
	return {
		year: parseInt(dateParts[0]),
		month: parseInt(dateParts[1]),
		day: parseInt(dateParts[2]),
	}
}

export const calcNightsCount = (
	checkIn?: string,
	checkOut?: string
): number => {
	if (!checkIn || !checkOut) return 1
	return Math.abs(
		Math.round(
			(Date.parse(checkOut) - Date.parse(checkIn)) / (1000 * 60 * 60 * 24)
		)
	)
}

export const roomParamToAdultsChildren = (
	room?: string[] | string
): RoomAllocation => {
	const allocations = room
		? (Array.isArray(room) ? room : [room]).map((r) => {
				const [adults, children = ''] = r.split(ROOM_GUESTS_SEPARATOR)
				const childAges = children
					? children.split(',').map((age) => Number(age))
					: []

				return {
					adults: parseInt(adults) || 1,
					childAge: childAges,
				}
		  })
		: [DEFAULT_ALLOCATION]
	const roomAllocation: RoomAllocation = {
		rooms: 0,
		adults: 0,
		childrenAges: [],
	}
	allocations.forEach((alloc) => {
		roomAllocation.rooms++
		roomAllocation.adults += alloc.adults
		roomAllocation.childrenAges!.push(...alloc.childAge)
	})
	if (!roomAllocation.childrenAges?.length) delete roomAllocation.childrenAges
	return roomAllocation
}

export const isPollComplete = (
	pollStatus: HotelSearchResponseStatusEnum
): boolean =>
	pollStatus === HotelSearchResponseStatusEnum.RESULT_STATUS_COMPLETE ||
	pollStatus === HotelSearchResponseStatusEnum.RESULT_STATUS_INCOMPLETE ||
	pollStatus === HotelSearchResponseStatusEnum.RESULT_STATUS_FAILED

export const createSearchParamsToKey = (csParams: CreateSearchParams): string =>
	JSON.stringify(csParams)

export const isSessionExpiredError = (error: AxiosError): boolean => {
	if (error.response?.status !== 400) return false
	const message = (error.response?.data as any)?.message
	if (typeof message !== 'string') return false
	return message.includes('expired')
}
